import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import { EloButton } from '@elo-ui/components/elo-button'
import { LoadingMask } from '@elo-kit/components'

import {
  CABINET_APPS_PATH,
  DEFAULT_PARTNER_APP_IFRAME_SANDBOX,
  PARTNER_APP_IDENTITY_TOKEN_MESSAGE,
  PARTNER_APP_IDENTITY_TOKEN_REQUEST_MESSAGE,
} from 'constants/partnerApps.constants'
import { ContentWidget } from 'ui/content-widget/ContentWidget'
import { TopBar } from 'ui/top-bar/TopBar'
import { buildPartnerAppUrl, addPartnerAppBreadcrumb } from 'shared/utils/partnerApps.utils'
import { useCabinetStores } from '../../hooks/use-cabinet-stores'

import './PartnerAppFrame.scss'

export const PartnerAppFrame: React.FC = observer(() => {
  const { partnerSlug } = useParams<{ partnerSlug: string }>()
  const { partnerAppsStore, identityTokensStore, sellerStore, experimentsStore } = useCabinetStores()
  const app = partnerAppsStore.findBySlug(partnerSlug)
  const resolveIdentityToken = identityTokensStore.resolveToken
  const sellerId = String(sellerStore.item.id)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const retry = useCallback(() => {
    setToken(null)
    setError(null)
    setRetryCount((value) => value + 1)
  }, [])

  useEffect(() => {
    let isCancelled = false

    if (!app || app.renderingType !== 'embedded') {
      return () => {
        isCancelled = true
      }
    }

    setToken(null)
    setError(null)

    resolveIdentityToken({
      context: 'seller',
      sellerId,
      partnerSlug: app.partnerSlug,
    })
      .then((resolvedToken) => {
        if (isCancelled) {
          return
        }

        if (!resolvedToken) {
          setError(new Error(I18n.t('react.cabinet.partner_apps.token_error_description')))

          return
        }

        setToken(resolvedToken)
      })
      .catch((identityTokenError) => {
        if (!isCancelled) {
          addPartnerAppBreadcrumb('partner app identity token fetch failed', app)
          setError(identityTokenError)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [app, retryCount, resolveIdentityToken, sellerId])

  useEffect(() => {
    if (!app || app.renderingType !== 'embedded' || !token) {
      return
    }

    const partnerAppOrigin = new URL(app.url).origin
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== partnerAppOrigin) {
        return
      }

      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || event.source !== iframeWindow) {
        return
      }

      if (event.data?.type !== PARTNER_APP_IDENTITY_TOKEN_REQUEST_MESSAGE) {
        return
      }

      iframeWindow.postMessage(
        {
          type: PARTNER_APP_IDENTITY_TOKEN_MESSAGE,
          identityToken: token,
        },
        partnerAppOrigin
      )
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [app, token])

  if (!app || app.renderingType !== 'embedded') {
    return <Redirect to={CABINET_APPS_PATH} />
  }

  if (error) {
    return (
      <>
        <TopBar flat title={app.name} />
        <ContentWidget className='partner-app-frame-page partner-app-frame-page--centered'>
          <div className='partner-app-frame-page__state' data-testid='partner-app-frame-error'>
            <h1>{I18n.t('react.cabinet.partner_apps.token_error_title')}</h1>
            <p>{I18n.t('react.cabinet.partner_apps.token_error_description')}</p>
            <EloButton dataTestId='partner-app-frame-retry' onClick={retry}>
              {I18n.t('react.shared.button.retry')}
            </EloButton>
          </div>
        </ContentWidget>
      </>
    )
  }

  if (!token) {
    return (
      <>
        <TopBar flat title={app.name} />
        <ContentWidget className='partner-app-frame-page partner-app-frame-page--centered'>
          <div data-testid='partner-app-frame-loading'>
            <LoadingMask />
          </div>
        </ContentWidget>
      </>
    )
  }

  const iframeSrc = buildPartnerAppUrl(app)

  return (
    <>
      <TopBar flat title={app.name} />
      <ContentWidget className='partner-app-frame-page partner-app-frame-page--iframe'>
        <iframe
          ref={iframeRef}
          title={app.name}
          src={iframeSrc}
          sandbox={DEFAULT_PARTNER_APP_IFRAME_SANDBOX}
          referrerPolicy='strict-origin-when-cross-origin'
          allow='clipboard-read; clipboard-write'
          className='partner-app-frame-page__iframe rr-block'
          onError={() => {
            experimentsStore.logEvent('partner_app_iframe_load_error', app.partnerSlug, {
              partnerSlug: app.partnerSlug,
            })
            addPartnerAppBreadcrumb('partner app iframe load error', app)
          }}
        />
      </ContentWidget>
    </>
  )
})

export default PartnerAppFrame
